/**
 * Represents a user with various properties such as email, transaction details, and custom fields.
 */
export class SoliticsUser {
    /**
     * Constructor for initializing a User instance with optional properties.
     *
     * @param {Object} params - Object containing optional user properties.
     * @param {string} [params.email] - The user's email address.
     * @param {string} [params.keyType] - The type of the user's key.
     * @param {string} [params.keyValue] - The value of the user's key.
     * @param {string} [params.popupToken] - The popup token associated with the user.
     * @param {string} [params.memberId] - The user's member ID.
     * @param {string} [params.brand] - The brand associated with the user.
     * @param {string} [params.branch] - The branch associated with the user.
     * @param {number} [params.amount] - The amount of a transaction related to the user.
     * @param {string} [params.customFields] - Custom fields associated with the user.
     */
    constructor({ email, keyType, keyValue, popupToken, memberId, brand, branch, amount, customFields = SoliticsUser.DEFAULT_CUSTOM_FIELDS, } = {}) {
        this._customFields = SoliticsUser.DEFAULT_CUSTOM_FIELDS;
        this._email = email;
        this._keyType = keyType;
        this._keyValue = keyValue;
        this._popupToken = popupToken;
        this._memberId = memberId;
        this._brand = brand;
        this._branch = branch;
        this._amount = amount;
        this._customFields = customFields;
    }
    /**
     * Gets the user's email address.
     * @returns {string | undefined} The email address of the user.
     */
    get email() {
        return this._email;
    }
    /**
     * Sets the user's email address.
     * @param {string | undefined} value - The new email address.
     */
    set email(value) {
        this._email = value;
    }
    /**
     * Gets the type of the user's key.
     * @returns {string | undefined} The type of the key.
     */
    get keyType() {
        return this._keyType;
    }
    /**
     * Sets the type of the user's key.
     * @param {string | undefined} value - The new key type.
     */
    set keyType(value) {
        this._keyType = value;
    }
    /**
     * Gets the value of the user's key.
     * @returns {string | undefined} The value of the key.
     */
    get keyValue() {
        return this._keyValue;
    }
    /**
     * Sets the value of the user's key.
     * @param {string | undefined} value - The new key value.
     */
    set keyValue(value) {
        this._keyValue = value;
    }
    /**
     * Gets the popup token associated with the user.
     * @returns {string | undefined} The popup token.
     */
    get popupToken() {
        return this._popupToken;
    }
    /**
     * Sets the popup token associated with the user.
     * @param {string | undefined} value - The new popup token.
     */
    set popupToken(value) {
        this._popupToken = value;
    }
    /**
     * Gets the user's member ID.
     * @returns {string | undefined} The member ID.
     */
    get memberId() {
        return this._memberId;
    }
    /**
     * Sets the user's member ID.
     * @param {string | undefined} value - The new member ID.
     */
    set memberId(value) {
        this._memberId = value;
    }
    /**
     * Gets the brand associated with the user.
     * @returns {string | undefined} The brand name.
     */
    get brand() {
        return this._brand;
    }
    /**
     * Sets the brand associated with the user.
     * @param {string | undefined} value - The new brand name.
     */
    set brand(value) {
        this._brand = value;
    }
    /**
     * Gets the branch associated with the user.
     * @returns {string | undefined} The branch name.
     */
    get branch() {
        return this._branch;
    }
    /**
     * Sets the branch associated with the user.
     * @param {string | undefined} value - The new branch name.
     */
    set branch(value) {
        this._branch = value;
    }
    /**
     * Gets the amount of a transaction related to the user.
     * @returns {number | undefined} The transaction amount.
     */
    get amount() {
        return this._amount;
    }
    /**
     * Sets the amount of a transaction related to the user.
     * @param {number | undefined} value - The new transaction amount.
     */
    set amount(value) {
        this._amount = value;
    }
    /**
     * Gets custom fields associated with the user.
     * @returns {string | undefined} The custom fields.
     */
    get customFields() {
        return this._customFields;
    }
    /**
     * Sets custom fields associated with the user.
     * @param {string | undefined} value - The new custom fields.
     */
    set customFields(value) {
        try {
            JSON.parse(value); // Ensure valid JSON
            this._customFields = value;
        }
        catch (_a) {
            throw new Error("Invalid JSON for customFields");
        }
    }
    /**
     * Converts the current SoliticsUser instance to a plain JavaScript object.
     *
     * @returns {Object} A plain object representing the SoliticsUser.
     */
    toObject() {
        return {
            email: this._email,
            keyType: this._keyType,
            keyValue: this._keyValue,
            popupToken: this._popupToken,
            memberId: this._memberId,
            brand: this._brand,
            branch: this._branch,
            amount: this._amount,
            customFields: this._customFields,
        };
    }
    /**
     * Serializes the current SoliticsUser instance to a JSON string.
     *
     * @returns {string} A JSON string representing the SoliticsUser.
     */
    toJSON() {
        return JSON.stringify(this.toObject());
    }
    /**
     * Creates a new SoliticsUser instance from a JSON string.
     *
     * @param {string} json - A JSON string representing a SoliticsUser.
     * @returns {SoliticsUser} A new instance of SoliticsUser.
     */
    static fromJSON(json) {
        const data = JSON.parse(json);
        return new SoliticsUser({
            email: data.email,
            keyType: data.keyType,
            keyValue: data.keyValue,
            popupToken: data.popupToken,
            memberId: data.memberId,
            brand: data.brand,
            branch: data.branch,
            amount: data.amount,
            customFields: data.customFields,
        });
    }
    /**
     * Checks if this SoliticsUser instance is equal to another.
     *
     * @param {SoliticsUser} other - Another SoliticsUser instance to compare.
     * @returns {boolean} True if the instances are equal, false otherwise.
     */
    equals(other) {
        if (!(other instanceof SoliticsUser)) {
            return false;
        }
        return (this._email === other.email &&
            this._keyType === other.keyType &&
            this._keyValue === other.keyValue &&
            this._popupToken === other.popupToken &&
            this._memberId === other.memberId &&
            this._brand === other.brand &&
            this._branch === other.branch &&
            this._amount === other.amount &&
            this._customFields === other.customFields);
    }
}
/**
 * Static constant for the default value of custom fields.
 */
SoliticsUser.DEFAULT_CUSTOM_FIELDS = '{}';
